import { View } from "@/components/ui/view";
import { Text } from "@/components/ui/text";
import { HStack } from "@/components/ui/hstack";
import { Divider } from "@/components/ui/divider";
import { Button } from "@/components/ui/button";
import { GlobalState } from '@/containers/GlobalState';
import { Image } from "@/components/ui/image";
import { switchScreen } from "../../../hooks/navigate";

import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import { useDispatch } from "react-redux";
import ReactFlow, {
    addEdge,
    applyEdgeChanges,
    applyNodeChanges,
    Background,
    getConnectedEdges,
    MarkerType,
    useNodesInitialized,
    useReactFlow
} from 'reactflow';
import i18n from 'i18n-js';
import _ from 'lodash';
import {MediaContext} from '../media/MediaContext';
import WorkflowStartNode from '../nodes/WorkflowStartNode';
import EndNode from '../nodes/EndNode';
import WaitNode from "../nodes/WaitNode";
import DecideNode from "../nodes/DecideNode";
import ChecklistNode from "../nodes/ChecklistNode";
import DoNode from "../nodes/DoNode";
import ValidationPopover from "../basic/ValidationPopover";
import saving from "../../algorithms_and_functions/backend_requests/saving";
import {guid7} from "@/containers/GlobalState";
import AddStepButton from "../basic/AddStepButton";
import FlowControls from "../basic/FlowControls";
import { IconM } from '@/components/media/IconsV2';
import getLayoutedNodes from "../../algorithms_and_functions/layoutView";
import AiPrompt from "../ai/AiPrompt";
import { exporting } from "@/stores/exporting";

const nodeTypes = {
    workflowStartNode: WorkflowStartNode,
    endNode: EndNode,
    doNode: DoNode,
    waitNode: WaitNode,
    decideNode: DecideNode,
    checklistNode: ChecklistNode
}

const placements = [
    'SIMPLE',
    'LINEAR_SEGMENTS',
    'BRANDES_KOEPF',
    'NETWORK_SIMPLEX',
]



function DefaultHeader({ state, addNode = ()=>{}, undo = ()=>{}, redo = ()=>{}, onOpenAiMenu = ()=>{}, onOpenSimulator = ()=>{}, fitView, flowId, allIssues, setAllIssues }) {

    return (<>
        <HStack className="absolute top-1 left-1">
            <HStack space='sm' hardShadow='3' className="px-3 bg-secondary-50 rounded-md items-center shadow-md">
                <HStack space='sm' className="py-2">
                    <Text className="font-[700] text-secondary-900 self-center mx-3">{i18n.t('workflowEdit.addStep')}:</Text>
                    <AddStepButton nodeType='doNode' stepType='do' addNode={addNode} />
                    <AddStepButton nodeType='checklistNode' stepType='fetch' addNode={addNode} />
                    <AddStepButton nodeType='waitNode' stepType='wait' addNode={addNode} />
                    <AddStepButton nodeType='decideNode' stepType='decide' addNode={addNode} />
                </HStack>
                <Divider orientation='vertical' className="h-8" />
                <HStack space='xs'>
                    <Button onPress={() => undo()} isDisabled={state.past.length === 0} accessibilityLabel={i18n.t('studio.flow.undo')} className="h-[34px] bg-primary-900 px-1">
                        <IconM className='text-secondary-50' name='undo' />
                    </Button>
                    <Button onPress={() => redo()} isDisabled={state.future.length === 0} accessibilityLabel={i18n.t('studio.flow.redo')} className="h-[34px] bg-primary-900 px-1">
                        <IconM className='text-secondary-50' name='redo' />
                    </Button>
                </HStack>
            </HStack>
            <HStack className="ml-7 px-3 bg-secondary-50 rounded-md items-center shadow-md">
                <Button onPress={() => onOpenAiMenu()} className="h-[34px] bg-primary-900 px-3 rounded-md">
                    <HStack className="flex-row items-center gap-2">
                        <Image
                            className="h-5 w-2.5"
                            resizeMode="contain"
                            source={require("../../../assets/images/ai_icon.png")}
                            alt=""
                        />
                        <Text className="font-normal text-secondary-50">
                            {i18n.t("studio.flow.aiSupport")}
                        </Text>
                    </HStack>
                </Button>
            </HStack>
        </HStack>

        <HStack className="absolute top-1 right-1 px-3 min-h-[50px] bg-secondary-50 rounded-md items-center shadow-md">
            <ValidationPopover fitView={fitView} save={state.present} flowId={flowId} allIssues={allIssues} setAllIssues={setAllIssues} onOpenSimulator={onOpenSimulator}
            />
        </HStack>
    </>)

}

function AiModeHeader({ state, undo = ()=>{}, redo = ()=>{}, onCloseAiMenu = ()=>{} }) {

    return (<HStack className="absolute top-1 self-center">
        <HStack className="px-3 bg-secondary-50 rounded-md items-center shadow-md">
            <HStack className="py-2 flex-row items-center gap-1">
                <Button onPress={() => undo()} isDisabled={state.past.length === 0}
                        accessibilityLabel={i18n.t("studio.flow.undo")}
                        className="h-[34px] bg-primary-900 px-1 rounded-md disabled:opacity-50"
                >
                    <IconM className="text-secondary-50" name="undo" />
                </Button>

                <Button
                    onPress={() => redo()} isDisabled={state.future.length === 0}
                    accessibilityLabel={i18n.t("studio.flow.redo")}
                    className="h-[34px] bg-primary-900 px-1 rounded-md disabled:opacity-50"
                >
                    <IconM className="text-secondary-50" name="redo" />
                </Button>
            </HStack>

            <Divider orientation="vertical" className="h-8 bg-secondary-200 mx-2" />

            <HStack className="py-2">
                <Button onPress={() => onCloseAiMenu()} className="h-[34px] bg-red-700 px-3 rounded-md">
                    <HStack className="flex-row items-center gap-2">
                        <Image className="h-5 w-2.5" resizeMode="contain"
                               source={require("../../../assets/images/ai_icon.png")} alt=""/>
                        <Text className="font-normal text-secondary-50">
                            {i18n.t('studio.flow.aiFinish')}
                        </Text>
                    </HStack>
                </Button>
            </HStack>
        </HStack>
    </HStack>)

}

export default function Flow({ navigation, workflow }) {
    console.log('FLOW', workflow)
    const flowId = workflow.id
    const dispatch = useDispatch()
    const [flow, setFlow] = useState(_.cloneDeep(workflow.flowData))

    const nodesInitialized = useNodesInitialized();
    const layoutPendingRef = useRef(false)

    const { inAColors } = useContext(GlobalState);
    const { getNode, getNodes, getEdges, setNodes, fitView, getViewport } = useReactFlow();
    const connectionLineStyle = { strokeWidth: 2, stroke: inAColors.primary[900] }

    useEffect(() => {
        for (const node of flow.nodes) {
            node.data.updateNode = updateNode
            node.data.copyNode = addNode
            node.data.disabled = false
            node.deletable = node.id !== 'startOfWorkflow' && node.id !== 'endOfWorkflow'
        }
        console.log('NODES')
    }, [flow])

    const [state, setState] = useState({
        present: flow,
        past: [],
        future: [],
        lastNodesChange: []
    })
    const [media, setMedia] = useState({})
    const [allIssues, setAllIssues] = useState([])
    const [nodeHighlighting, setNodeHighlighting] = useState({})
    const [pIdx, setPIdx] = useState(0)
    const [aiMenuOpen, setAiMenuOpen] = useState(false)
    const stateRef = useRef(state.present)

    useEffect(() => {
        console.log("Present workflow")
        console.log(state.present)
    }, [state.present])

    useEffect(() => {
        if (!layoutPendingRef.current)
            return

        const stillMissing = state?.present?.nodes?.some(n => !n.width || !n.height)
        if (stillMissing)
            return

        layoutPendingRef.current = false
        layoutView()
    }, [state?.present?.nodes])

    useEffect(() => {
        console.log('state')
        console.log(state)
    }, [state])

    useEffect(() => {
        stateRef.current = state.present
    }, [state.present])

    useEffect(() => {
        navigation.setOptions({ title: state.present.nodes[0].data.title, openModal: false, flowId: flowId })
    }, [state.present.nodes[0].data.title])

    useEffect(() => {
        let nodeHighlights = {}
        for (const issue of allIssues) {
            for (const node of issue.getAffectedNodesIds()) {
                nodeHighlights[node.id] = { ...nodeHighlights[node.id], ...issue.getHighlighting() }
            }
        }
        setNodeHighlighting(nodeHighlights)
    }, [allIssues])

    async function saveFlow(save = state.present) {
        console.log('XXX Save', flowId)
        return await saving({ ...workflow, flowData: save })
    }

    useEffect(() => {
        if (state.past.length > 0) {
            saveFlow().then((res) => {
                navigation.setOptions({ saved: res && res.workflowData })
            })
        } else {
            navigation.setOptions({ saved: true })
        }
    }, [state.past])


    useEffect(() => {
        return navigation.addListener('beforeRemove', (e) => {
            e.preventDefault()
            saveFlow(stateRef.current).then((res) => {
                if (res && res.workflowData) {
                    navigation.dispatch(e.data.action)
                } else {
                    alert(i18n.t('studio.editor.savingAlert'))

                }
            })
        })
    }, [navigation])

    // new present state will be sent to the server!
    const pushAndUpdateNodes = (prevState, newNodes, changes) => {
        return ({
            present: { nodes: newNodes, edges: prevState.present.edges },
            past: [...prevState.past, _.cloneDeep(prevState.present)],
            future: [],
            lastNodesChange: changes
        })
    }

    const updateNodes = (prevState, newNodes, changes) => {
        return ({
            present: {
                nodes: newNodes,
                edges: prevState.present.edges
            },
            past: prevState.past,
            future: prevState.future,
            lastNodesChange: changes
        })
    }

    const onNodesChange = useCallback((changes) => {
        setState((prevState) => {
            const newNodes = applyNodeChanges(changes, prevState.present.nodes)

            // changes is add, remove, replace or first position change => add prevState to history
            if (!['select', 'position', 'dimensions'].includes(changes[0].type)
                || (changes[0].type == 'position' && changes[0].position && (prevState.lastNodesChange[0].type != 'position' || !prevState.lastNodesChange[0].position))) {
                return pushAndUpdateNodes(prevState, newNodes, changes)
            }
            // otherwise just update the nodes
            return updateNodes(prevState, newNodes, changes)
        })
    }, [])

    const addNode = (nodeType, data, id) => {
        setState((prevState) => {
            const viewPosition = getViewport()
            const copyNode = prevState.present.nodes.find(node => node.id == id)
            const pos = copyNode ? { x: copyNode.position.x + 350, y: copyNode.position.y } : { x: Math.floor(((viewPosition.x * -1) + 200 + (prevState.present.nodes.length * 10)) / viewPosition.zoom), y: Math.floor(((viewPosition.y * -1) + 100) / viewPosition.zoom) }
            const newNode = {
                id: guid7(),
                position: pos,
                data: {
                    title: data ? data.title : '',
                    description: data ? data.description : '',
                    media: [],
                    nodeType: nodeType,
                    updateNode: updateNode,
                    copyNode: addNode
                },
                type: nodeType
            }
            if (data && data.elements) {
                newNode.data.elements = data.elements.map(elem => ({ id: newNode.id + 'element' + guid7().substring(0, 7), title: elem.title, description: elem.description }))
            }
            // new present state will be sent to the server!
            return ({
                present: {
                    nodes: [...prevState.present.nodes, newNode],
                    edges: prevState.present.edges
                },
                past: [...prevState.past, _.cloneDeep(prevState.present)],
                future: [],
                lastNodesChange: prevState.lastNodesChange
            })
        })

    }

    // new present state will be sent to the server!
    const pushAndUpdateNodeData = (prevState, id, data, type) => {
        const newState = {
            present: {
                nodes: prevState.present.nodes.map((node) => {
                    if (node.id === id) {
                        let copyNode = _.cloneDeep(node)
                        copyNode.data = data
                        return copyNode
                    }
                    return node;
                }),
                edges: prevState.present.edges
            },
            past: [...prevState.past, _.cloneDeep(prevState.present)],
            future: [],
            lastNodesChange: [{ type: type }]
        }
        return newState
    }

    const updateNodeData = (prevState, id, data) => {
        return ({
            present: {
                nodes: prevState.present.nodes.map((node) => {
                    if (node.id === id) {
                        let copyNode = _.cloneDeep(node)
                        copyNode.data = data
                        return copyNode
                    }
                    return node;
                }),
                edges: prevState.present.edges
            },
            past: prevState.past,
            future: prevState.future,
            lastNodesChange: [{ type: 'data' }]
        })
    }

    const updateNode = (id, data, pushState) => {
        setState((prevState) => {
            // push is requested or first data change => add prevState to history
            return pushState || prevState.lastNodesChange[0].type !== 'data' ? pushAndUpdateNodeData(prevState, id, data, pushState ? 'history' : 'data') : updateNodeData(prevState, id, data)
        })
    }

    const edgeChangeHistory = (prevState, changes) => {
        return ({
            present: {
                nodes: prevState.present.nodes,
                edges: applyEdgeChanges(changes, prevState.present.edges)
            },
            past: [...prevState.past, _.cloneDeep(prevState.present)],
            future: [],
            lastNodesChange: prevState.lastNodesChange
        })
    }

    const onEdgesChange = useCallback((changes) => {
        setState((prevState) => {
            if (changes[0].type !== 'select') {
                return edgeChangeHistory(prevState, changes)
            }
            return ({
                present: {
                    nodes: prevState.present.nodes,
                    edges: applyEdgeChanges(changes, prevState.present.edges)
                },
                past: prevState.past,
                future: prevState.future,
                lastNodesChange: prevState.lastNodesChange
            })
        })
    }, [])

    const onConnect = useCallback((params) => {
        setState((prevState) => {
            return ({
                present: {
                    nodes: prevState.present.nodes,
                    edges: addEdge({
                        ...params,
                        type: 'smoothstep',
                        style: { strokeWidth: 2, stroke: inAColors.primary[900] },
                        markerEnd: {
                            type: MarkerType.ArrowClosed,
                            width: 20,
                            height: 20,
                            color: inAColors.primary[900],
                        },
                    }, prevState.present.edges)
                },
                past: [...prevState.past, _.cloneDeep(prevState.present)],
                future: [],
                lastNodesChange: prevState.lastNodesChange
            })
        })
    }, [])

    const onConnectStart = useCallback((__, { nodeId, handleType, handleId }) => {
        if (handleType === 'source') {
            const edges = getConnectedEdges([getNode(nodeId)], getEdges());
            if (edges.length > 0) {
                let deleteEdges = []
                edges.map(({ id, source, sourceHandle }) => {
                    if (source === nodeId && sourceHandle === handleId) {
                        deleteEdges.push({ id: id, type: 'remove' })
                    }
                })
                if (deleteEdges.length > 0) {
                    setState((prevState) => {
                        return edgeChangeHistory(prevState, deleteEdges)
                    })
                }
            }
        }
    }, [])

    const undo = () => {
        let pastElements = _.cloneDeep(state.past)
        const lastElements = pastElements.pop()
        const currentElements = _.cloneDeep(state.present)
        let futureElements = _.cloneDeep(state.future)
        futureElements.push(currentElements)

        setState({
            present: lastElements,
            past: pastElements,
            future: futureElements,
            lastNodesChange: [{ type: 'undo' }]
        })
    }

    const layoutView = () => {
        if (nodesInitialized) {
            const layoutNodes = async () => {
                const layoutedNodes = await getLayoutedNodes(
                    getNodes(),
                    getEdges(),
                    placements[pIdx]
                );
                //setPIdx((pIdx + 1) % placements.length)
                //setNodes(layoutedNodes);
                setState(prev => ({
                    present: { nodes: layoutedNodes, edges: prev.present.edges },
                    past: [...prev.past, _.cloneDeep(prev.present)],
                    future: [],
                    lastNodesChange: [{ type: 'layout' }],
                }));
                setTimeout(() => fitView(), 0);
            };
            layoutNodes();
        }
    }

    const redo = () => {
        let futureElements = _.cloneDeep(state.future)
        const lastElements = futureElements.pop()
        const currentElement = _.cloneDeep(state.present)
        let pastElements = _.cloneDeep(state.past)
        pastElements.push(currentElement)

        setState({
            present: lastElements,
            past: pastElements,
            future: futureElements,
            lastNodesChange: [{ type: 'redo' }]
        })
    }

    // Creates a styled edge with your marker/colors
    function makeEdge(source, target, sourceHandle, targetHandle) {
        return {
            id: 'e_' + guid7(),
            source,
            target,
            sourceHandle,
            targetHandle,
            type: 'smoothstep',
            style: { strokeWidth: 2, stroke: connectionLineStyle.stroke },
            markerEnd: {
                type: MarkerType.ArrowClosed,
                width: 20,
                height: 20,
                color: connectionLineStyle.stroke,
            },
        };
    }

    async function replaceWithAiGraph(ai) {
        const START_ID = 'startOfWorkflow';
        const END_ID = 'endOfWorkflow';

        const NODE_WIDTH = 340

        // Handle IDs used by your node components (adjust if your nodes differ)
        const HANDLES = {
            common: { in: 'input', out: 'output' },
            decide: { in: 'input', yes: 'yes', no: 'no' },
        };

        // Reuse existing start/end nodes if present; otherwise create defaults
        const existingStart = state.present.nodes.find(n => n.id === START_ID);
        const existingEnd = state.present.nodes.find(n => n.id === END_ID);

        const startNode = {
            id: START_ID,
            type: 'workflowStartNode',
            position: { x: 0, y: 0 },
            deletable: false,
            width: NODE_WIDTH,
            data: {
                title: (ai && ai.title) || 'Start',
                description: (ai && ai.text) || '',
                media: [],
                nodeType: 'workflowStartNode',
                updateNode,
                copyNode: addNode,
                disabled: false,
            },
        };

        const endNode = {
            id: END_ID,
            type: 'endNode',
            position: { x: 0, y: 0 },
            deletable: false,
            width: NODE_WIDTH,
            data: {
                title: 'End',
                description: '',
                media: [],
                nodeType: 'endNode',
                updateNode,
                copyNode: addNode,
                disabled: false,
            },
        };

        // Map AI step.type -> your nodeTypes
        const AI_TYPE_TO_NODE = {
            0: 'doNode',
            1: 'checklistNode',
            2: 'waitNode',
            3: 'decideNode',
        };

        // Build AI nodes from steps (ordered) and create a ref → nodeId map
        const steps = Array.isArray(ai && ai.steps) ? ai.steps : [];
        const idForStep = (ref) => `ai_${ref}_${guid7()}`;

        const refToNodeId = {};
        const aiNodes = steps.map((s, i) => {
            const ref = (s && s.ref) || `step${i + 1}`;
            const id = idForStep(ref);
            refToNodeId[ref] = id;

            const nodeType = AI_TYPE_TO_NODE[s.type] || 'doNode';

            const data = {
                _aiRef: ref,
                title: s.title || `Step ${i + 1}`,
                description: s.text || '',
                media: [],
                nodeType,
                updateNode,
                copyNode: addNode,
                disabled: false,
            };

            if (nodeType === 'checklistNode' && Array.isArray(s.check)) {
                data.elements = s.check.map(item => ({
                    id: id + '_element_' + guid7().substring(0, 7),
                    title: (item && item.title) || '',
                    description: (item && item.text) || '',
                }));
            }

            if (nodeType === 'waitNode' && typeof s.time === 'number') {
                data.timer = (Math.floor(s.time / 3600) < 10 ? '0' : '') + Math.floor(s.time / 3600) + (Math.floor((s.time % 3600) / 60) < 10 ? ':0' : ':') + Math.floor((s.time % 3600) / 60) + (Math.floor((s.time % 3600) % 60) < 10 ? ':0' : ':') + Math.floor((s.time % 3600) % 60)
            }

            // Keep ref-based routing temporarily; we'll resolve to node ids later
            if (nodeType === 'decideNode') {
                data._aiYesRef = typeof s.yes === 'string' ? s.yes : null;
                data._aiNoRef  = typeof s.no  === 'string' ? s.no  : null;
            }
            if (typeof s.goto === 'string') {
                data._aiGotoRef = s.goto;
            }

            return {
                id,
                type: nodeType,
                position: { x: 0, y: 0 }, // will be laid out later
                width: NODE_WIDTH,
                deletable: true,
                data,
            };
        });

        const explicitRefTargets = new Set();
        steps.forEach(s => {
            if (typeof s.yes === 'string') explicitRefTargets.add(s._aiRef);
            if (typeof s.no  === 'string') explicitRefTargets.add(s._aiRef);
            if (typeof s.goto=== 'string') explicitRefTargets.add(s._aiRef);
        });

        // Build edges
        const edges = [];

        // If there are AI nodes, wire: Start -> first AI node
        if (aiNodes.length > 0) {
            edges.push(
                makeEdge(START_ID, aiNodes[0].id, HANDLES.common.out, HANDLES.common.in)
            );
        } else {
            // No AI nodes: wire Start directly to End and finish
            setState(prev => ({
                present: {
                    nodes: [startNode, endNode],
                    edges: [makeEdge(START_ID, END_ID, HANDLES.common.out, HANDLES.common.in)],
                },
                past: [...prev.past, _.cloneDeep(prev.present)],
                future: [],
                lastNodesChange: [{ type: 'ai-replace' }],
            }));
            setTimeout(() => fitView(), 0);
            return;
        }

        // Pass 1: default linear edges (current -> next) except decide nodes and nodes with goto
        for (let i = 0; i < aiNodes.length - 1; i++) {
            const cur  = aiNodes[i];
            const next = aiNodes[i + 1];

            const curRef = steps[i]?.ref;                  // <-- current step’s ref
            const isDecide = cur.type === 'decideNode';
            const hasGoto  = !!cur.data._aiGotoRef;
            const isExplicitTarget = explicitRefTargets.has(curRef);

            if (!isDecide && !hasGoto && !isExplicitTarget) {
                edges.push(makeEdge(cur.id, next.id, HANDLES.common.out, HANDLES.common.in));
            }
        }

        // Pass 2: add decide branches using yes/no handle IDs (ref → node id)
        aiNodes.forEach((node, idx) => {
            if (node.type !== 'decideNode') return;

            // Remove any linear edges that may have been added from this node
            for (let i = edges.length - 1; i >= 0; i--) {
                if (edges[i].source === node.id) edges.splice(i, 1);
            }

            const yesRef = node.data._aiYesRef;
            const noRef  = node.data._aiNoRef;

            const fallbackNextId = aiNodes[idx + 1] ? aiNodes[idx + 1].id : END_ID;

            const yesTargetId = yesRef && refToNodeId[yesRef] ? refToNodeId[yesRef] : fallbackNextId;
            const noTargetId  = noRef  && refToNodeId[noRef]  ? refToNodeId[noRef]  : END_ID;

            edges.push(
                makeEdge(node.id, yesTargetId, HANDLES.decide.yes, HANDLES.decide.in)
            );
            edges.push(
                makeEdge(node.id, noTargetId, HANDLES.decide.no, HANDLES.decide.in)
            );

            delete node.data._aiYesRef;
            delete node.data._aiNoRef;
        });

        // Pass 3: handle `goto` jumps on non-decide nodes (ref → node id)
        aiNodes.forEach((node) => {
            if (node.type === 'decideNode') return;

            const gotoRef = node.data._aiGotoRef;
            if (!gotoRef) return;

            // Remove any linear edge from this node (if one exists)
            for (let i = edges.length - 1; i >= 0; i--) {
                if (edges[i].source === node.id) edges.splice(i, 1);
            }

            const targetId = refToNodeId[gotoRef] || END_ID;
            edges.push(
                makeEdge(node.id, targetId, HANDLES.common.out, HANDLES.common.in)
            );

            delete node.data._aiGotoRef;
        });

        // Connect every terminal AI node to End
        const aiIdsWithOutgoing = new Set(edges.map(e => e.source));
        aiNodes.forEach(n => {
            if (!aiIdsWithOutgoing.has(n.id)) {
                edges.push(
                    makeEdge(n.id, END_ID, HANDLES.common.out, HANDLES.common.in)
                );
            }
        });

        // Compose final nodes list
        const nextNodes = [startNode, ...aiNodes, endNode];


        // Layout + fit (best-effort)
        let layoutedNodes = nextNodes;
        try {
            layoutedNodes = await getLayoutedNodes(nextNodes, edges, placements[pIdx]);
        } catch (e) {
            console.warn('Layout failed, using default positions', e);
        }
        setPIdx((pIdx + 1) % placements.length);

        layoutPendingRef.current = true
        // Push to history (triggers autosave via your effect)
        setState(prev => ({
            present: { nodes: layoutedNodes, edges },
            past: [...prev.past, _.cloneDeep(prev.present)],
            future: [],
            lastNodesChange: [{ type: 'ai-replace' }],
        }));
    }

    const openSimulator = () => {
        const taskid = dispatch(exporting(state.present, flowId, true))
        if (!taskid) {
            return
        }
        switchScreen(navigation, 'ModalSimulator', {
            screen: 'MainStack',
            params: { screen: 'ShowWorkflow2', params: { taskid, isSimulator: true } },
            taskid
        })
    }

    return (
        <View className="flex-1">
            <MediaContext.Provider value={{ media, setMedia, flowId, nodeHighlighting }}>
                <ReactFlow
                    nodes={state.present.nodes}
                    onNodesChange={onNodesChange}
                    nodeTypes={nodeTypes}
                    edges={state.present.edges}
                    onEdgesChange={onEdgesChange}
                    onConnect={onConnect}
                    onConnectStart={onConnectStart}
                    connectionLineStyle={connectionLineStyle}
                    noDragClassName='nodrag, [data-nodrag]'
                    preventScrolling={false}
                    // zoomOnScroll={false}
                    // noPanClassName='nopan, [data-nopan]'
                    fitView
                >
                    <Background />
                    <FlowControls layoutView={layoutView} />
                </ReactFlow>

                {
                    aiMenuOpen
                    ?
                        <AiModeHeader
                            state={state}
                            undo={undo}
                            redo={redo}
                            onCloseAiMenu={() => setAiMenuOpen(false)}
                        />
                    :
                        <DefaultHeader
                            state={state}
                            addNode={addNode}
                            undo={undo}
                            redo={redo}
                            onOpenAiMenu={() => setAiMenuOpen(true)}
                            fitView={fitView}
                            flowId={flowId}
                            allIssues={allIssues}
                            setAllIssues={setAllIssues}
                            onOpenSimulator={openSimulator}
                        />
                }

                <AiPrompt visible={aiMenuOpen} onClose={() => setAiMenuOpen(false)}
                          onApplyAiGraph={aiJson => replaceWithAiGraph(aiJson)} getCurrentGraph={() => state.present} />
            </MediaContext.Provider>
        </View>
    );
}
